import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import BarChart from 'common/charts/GoogleCharts/BarChart';
import colors from 'common/styles/colors';
import { userActions } from 'onboarding/user.actions';
import {
  openEventsActivityFilters,
  seedActivityListFilters
} from 'home/Widget/helpers/helpers';
import useOrgWidgetData from '../hooks/useOrgWidgetData';
import { navigateToSite } from '../utils/orgSiteNavigate';
import { OrgWidgetDataState } from './OrgWidgetStates';
import { getOrgWidgetTitle, useResolveOrgWidgetMeta } from './orgWidgetMeta';

const WIDGET_NAME = 'org_open_events_by_site';

// Without an explicit filter Activity keeps the account's last-used one.
const openEventsSiteTarget = (accountId) => {
  seedActivityListFilters(accountId, openEventsActivityFilters());
  return '/activity';
};

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const OrgOpenEventsBySite = ({
  organisationId,
  widgetsMetadata,
  title,
  selectAccount
}) => {
  const { t } = useTranslation();
  const meta = useResolveOrgWidgetMeta(widgetsMetadata, WIDGET_NAME);
  const widgetTitle = getOrgWidgetTitle(title, meta);
  const { data, loading, error, reload } = useOrgWidgetData(organisationId, meta);

  const sites = (data && data.sites) || [];

  const chartData = useMemo(() => {
    if (!sites.length) return null;
    const header = [
      t('orgView.widgets.site', 'Site'),
      t('orgView.events.open', 'Open events')
    ];
    const rows = sites.map(site => [site.name, site.open_events || 0]);
    return [header, ...rows];
  }, [sites, t]);

  const handleChartClick = useCallback(({ label }) => {
    const site = sites.find(s => s.name === label);
    if (site && site.account_id != null) {
      navigateToSite(selectAccount, site.account_id, openEventsSiteTarget);
    }
  }, [sites, selectAccount]);

  return (
    <OrgWidgetDataState
      title={widgetTitle}
      loading={loading}
      hasError={error}
      onRetry={reload}
      isEmpty={!chartData}
      emptyMessage={t('orgView.events.noData', 'No open events data')}
      emptyScrollable={false}
    >
      <Wrapper>
        <BarChart
          title={widgetTitle}
          data={chartData}
          colors={[colors.normal, colors.warning, colors.critical]}
          orientation="vertical"
          isStacked={false}
          isInteractive
          handleClick={handleChartClick}
          chartArea={{ width: '75%', height: '72%', left: '12%', bottom: '18%' }}
          bar={{ groupWidth: '55%' }}
          yLabelFontSize={11}
        />
      </Wrapper>
    </OrgWidgetDataState>
  );
};

OrgOpenEventsBySite.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string,
  selectAccount: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      selectAccount: userActions.selectAccount
    },
    dispatch
  );

OrgOpenEventsBySite.defaultProps = {
  widgetsMetadata: null,
  title: null
};

export default connect(null, mapDispatchToProps)(OrgOpenEventsBySite);
